// SQLite adapter. Works over any executor with exec/run/all — Node's built-in
// `node:sqlite` (tests, CLI tools, sync server) today; the Tauri desktop shell runs the
// identical DDL and changeset semantics in Rust (apps/desktop/src-tauri/src/db.rs).
import { emptyDataset, TABLE_NAMES, type Changeset, type Dataset, type TableName } from '@vero/domain';
import { StorageError, fromBase64, toBase64, type FileMeta, type SnapshotMeta, type StorageAdapter } from './adapter.ts';
import { decodeRow, encodeRow, type SqlValue } from './codec.ts';
import { LATEST_VERSION, MIGRATIONS } from './migrations.ts';

export interface SqlExecutor {
  exec(sql: string): void;
  run(sql: string, params: SqlValue[]): void;
  all(sql: string, params?: SqlValue[]): Record<string, unknown>[];
}

/** Wrap a node:sqlite DatabaseSync */
export function nodeSqliteExecutor(db: { exec(sql: string): void; prepare(sql: string): { run(...p: SqlValue[]): unknown; all(...p: SqlValue[]): unknown[] } }): SqlExecutor {
  const cache = new Map<string, ReturnType<typeof db.prepare>>();
  const stmt = (sql: string) => { let s = cache.get(sql); if (!s) { s = db.prepare(sql); cache.set(sql, s); } return s; };
  return {
    exec: (sql) => db.exec(sql),
    run: (sql, params) => { stmt(sql).run(...params); },
    all: (sql, params = []) => stmt(sql).all(...params) as Record<string, unknown>[],
  };
}

export class SqliteAdapter implements StorageAdapter {
  readonly engine: string;
  private x: SqlExecutor;
  constructor(executor: SqlExecutor, engine = 'sqlite') { this.x = executor; this.engine = engine; }

  private tx<T>(fn: () => T): T {
    this.x.exec('BEGIN IMMEDIATE');
    try { const r = fn(); this.x.exec('COMMIT'); return r; }
    catch (e) { try { this.x.exec('ROLLBACK'); } catch { /* already rolled back */ } throw e instanceof StorageError ? e : new StorageError('storage.failed', String((e as Error)?.message ?? e), { cause: e }); }
  }

  async open() {
    this.x.exec('PRAGMA journal_mode = WAL');
    this.x.exec('PRAGMA synchronous = FULL');
    this.x.exec('CREATE TABLE IF NOT EXISTS "_meta" ("key" TEXT PRIMARY KEY NOT NULL, "value" TEXT) STRICT');
    const row = this.x.all(`SELECT value FROM "_meta" WHERE key='schemaVersion'`)[0];
    const current = row ? Number(JSON.parse(String(row.value))) : 0;
    if (current > LATEST_VERSION) throw new StorageError('storage.newer_schema', `Database schema ${current} is newer than this app (${LATEST_VERSION})`);
    for (const m of MIGRATIONS) {
      if (m.version <= current) continue;
      this.tx(() => {
        for (const s of m.sql) this.x.exec(s);
        this.x.run(`INSERT INTO "_meta"(key,value) VALUES('schemaVersion',?) ON CONFLICT(key) DO UPDATE SET value=excluded.value`, [JSON.stringify(m.version)]);
        this.x.run(`INSERT INTO "_meta"(key,value) VALUES(?,?) ON CONFLICT(key) DO UPDATE SET value=excluded.value`, [`migration.${m.version}`, JSON.stringify({ name: m.name, at: new Date().toISOString() })]);
      });
    }
    return { schemaVersion: LATEST_VERSION, created: current === 0 };
  }

  async loadAll(): Promise<Dataset> {
    const ds = emptyDataset() as Record<TableName, unknown[]>;
    for (const t of TABLE_NAMES) ds[t] = this.x.all(`SELECT * FROM "${t}"`).map((r) => decodeRow(t, r));
    return ds as Dataset;
  }

  private upsert(table: TableName, row: Record<string, unknown>) {
    const { cols, values } = encodeRow(table, row);
    const sql = `INSERT INTO "${table}" (${cols.map((c) => `"${c}"`).join(',')}) VALUES (${cols.map(() => '?').join(',')}) ON CONFLICT(id) DO UPDATE SET ${cols.filter((c) => c !== 'id').map((c) => `"${c}"=excluded."${c}"`).join(',')}`;
    this.x.run(sql, values);
  }

  async commit(cs: Changeset) {
    this.tx(() => {
      for (const d of cs.deletes) this.x.run(`DELETE FROM "${d.table}" WHERE id=?`, [d.id]);
      for (const p of cs.puts) this.upsert(p.table, p.row as unknown as Record<string, unknown>);
    });
  }

  async replaceAll(ds: Dataset) {
    this.tx(() => {
      for (const t of TABLE_NAMES) this.x.exec(`DELETE FROM "${t}"`);
      for (const t of TABLE_NAMES) for (const r of ds[t] ?? []) this.upsert(t, r as unknown as Record<string, unknown>);
      // verify inside the transaction: counts must match exactly, else ROLLBACK
      for (const t of TABLE_NAMES) {
        const n = Number(this.x.all(`SELECT COUNT(*) AS n FROM "${t}"`)[0]!.n);
        if (n !== (ds[t] ?? []).length) throw new StorageError('storage.verify_failed', `${t}: expected ${(ds[t] ?? []).length}, stored ${n}`);
      }
    });
  }

  async getMeta<T>(key: string) {
    const r = this.x.all(`SELECT value FROM "_meta" WHERE key=?`, [key])[0];
    return r ? (JSON.parse(String(r.value)) as T) : undefined;
  }
  async setMeta(key: string, value: unknown) {
    this.x.run(`INSERT INTO "_meta"(key,value) VALUES(?,?) ON CONFLICT(key) DO UPDATE SET value=excluded.value`, [key, JSON.stringify(value)]);
  }
  async saveSnapshot(meta: SnapshotMeta, content: string) {
    this.x.run(`INSERT INTO "_snapshots"(id,meta,content) VALUES(?,?,?) ON CONFLICT(id) DO UPDATE SET meta=excluded.meta, content=excluded.content`, [meta.id, JSON.stringify(meta), content]);
  }
  async listSnapshots() {
    return this.x.all(`SELECT meta FROM "_snapshots"`).map((r) => JSON.parse(String(r.meta)) as SnapshotMeta).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }
  async readSnapshot(id: string) { const r = this.x.all(`SELECT content FROM "_snapshots" WHERE id=?`, [id])[0]; return r ? String(r.content) : undefined; }
  async deleteSnapshot(id: string) { this.x.run(`DELETE FROM "_snapshots" WHERE id=?`, [id]); }
  async putFile(meta: FileMeta, content: Uint8Array) { this.x.run(`INSERT INTO "_files"(id,meta,content) VALUES(?,?,?) ON CONFLICT(id) DO UPDATE SET meta=excluded.meta, content=excluded.content`, [meta.id, JSON.stringify(meta), toBase64(content)]); }
  async getFile(id: string) { const r = this.x.all(`SELECT meta, content FROM "_files" WHERE id=?`, [id])[0]; return r ? { meta: JSON.parse(String(r.meta)) as FileMeta, content: fromBase64(String(r.content)) } : undefined; }
  async deleteFile(id: string) { this.x.run(`DELETE FROM "_files" WHERE id=?`, [id]); }
  async listFiles() { return this.x.all(`SELECT meta FROM "_files"`).map((r) => JSON.parse(String(r.meta)) as FileMeta); }
  async close() {}
}
